/**
 * @file StandingOrders.js
 * @version 1.13.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-12-10
 * @modified 2026-10-30
 * @description Manage recurring standing orders for customers
 *
 * 1.13.0 (2026-10-30, Cam — rep order requests, 29 Oct): a rep gets "Request
 * a change" — in the read-only standing order on their phone, and in the row
 * menu on a laptop — the request form filled in from the standing order, sent
 * to the office (RepChangeRequestModal); never an edit.
 *
 * 1.12.0 (2026-10-28, Cam — rep mobile review): on a rep's phone each
 * standing order is a RepDocCard — the customer on its own line, then
 * "Weekly · Wednesdays · 18.00 T", then "Last 26/10 · next in 2 days"; Held
 * is a status word, not a badge laid over the name. A tap opens it read only
 * (RepStandingOrderView) — before, a tap only highlighted the row.
 *
 * 1.11.0 (2026-10-15, Cam): no order from a standing order whose product is
 * archived. "Create Customer Order" stays in the menu, marked, and opens
 * StandingOrderArchivedDialog instead (find out what replaces it, then Edit —
 * the product picker highlighted); picked PO lines for an archived product
 * are refused the same way; archived alternatives are left behind with a
 * note. A "Product archived" badge on the row, a toggle to show only those,
 * and their count in the header (utils/standingOrderProducts).
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './StandingOrders.css';
import './TableStyles.css';
import './ResizableColumns.css';
import Dropdown from './Dropdown';
import Input from './Input';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import StandingOrderModal from './StandingOrderModal';
import StandingOrderArchivedDialog from './StandingOrderArchivedDialog';
import ContactModal from './ContactModal';
import Modal from './Modal';
import RowActionsMenu from './RowActionsMenu';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import useResizableColumns from '../hooks/useResizableColumns';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import SortOptionsMenu, { useSortMenu } from './SortOptionsMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import TableContextMenu from './TableContextMenu';
import RepMobileFilterReset from './RepMobileFilterReset';
import useRepMobile from '../hooks/useRepMobile';
import RepDocCard, { RepDocCards } from './RepDocCard';
import RepStandingOrderView from './RepStandingOrderView';
import { repStandingOrderCard } from '../utils/repDocCards';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { withAdditives } from '../utils/additiveLabel';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import RepChangeRequestModal from './RepChangeRequestModal';
import { useAccess } from '../hooks/useAccess';
import { useReveal, revealFilters } from '../utils/revealInSearch';
import {
  createOrderBlock,
  mainProductArchived,
  mainProductOf,
  alternativesOf,
  droppedAlternativesNote,
  productLabel
} from '../utils/standingOrderProducts';

// Held first — paused, and the ones that want a decision — then active, then
// inactive at the bottom. The table sorts within each.
const STATUS_ORDER = { held: 0, active: 1, inactive: 2 };
const STATUS_LABEL = { active: 'Active', held: 'Held', inactive: 'Inactive' };
const statusOf = (order) => order.status || (order.is_active === false ? 'inactive' : 'active');

const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

/**
 * Columns filterable from their header. Keys must match the whitelist in
 * standingOrderService.searchStandingOrders; an unknown key narrows nothing.
 */
const COLUMN_FILTERS = {
  customer: { label: 'Customer' },
  product: { label: 'Product' },
  additive: { label: 'Additive' },
  schedule_type: { label: 'Type' },
  frequency: { label: 'Frequency' },
  status: { label: 'Status' },
  /* The Last/next column asked as a question. Faceting the dates themselves
     offers hundreds of values nobody can use; what people want to know is
     which standing orders have been left behind. */
  due_state: { label: 'Last / next due' }
};

/**
 * The product on this line is archived.
 *
 * A seasonal blend is archived once its season ends, but the standing order
 * naming it carries on generating deliveries against a product nobody is
 * buying in any more — and nothing on this screen said so. `.archived-badge`
 * is the same idiom the pricing and search tables use, so an archived thing
 * looks the same wherever it appears.
 *
 * Shown on inactive orders too: it is a fact about the product, not about the
 * schedule, and hiding it on a paused order would only make it a surprise when
 * somebody resumes one.
 */
function ArchivedProduct({ main = false }) {
  return main ? (
    <span
      className="archived-badge so-archived-product is-main"
      title="The main product is archived — no order can be made from this standing order until its product is changed"
    >
      Product archived
    </span>
  ) : (
    <span
      className="archived-badge so-archived-product"
      title="This alternative is archived — it is not offered when an order is made"
    >
      Archived
    </span>
  );
}

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight and the toolbar pill (utils/activeFilters.js).
 */
const CLEARED_FILTERS = { search: '', scheduleType: '', dayOfWeek: '', status: '', productArchived: false, limit: '50' };

function StandingOrders({ user, onNavigate }) {
  const [standingOrders, setStandingOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  // The edit window opened from the archived-product prompt: product picker highlighted.
  const [focusProduct, setFocusProduct] = useState(false);
  // "Create Customer Order" refused for an archived product: { order, block }.
  const [archivedPrompt, setArchivedPrompt] = useState(null);
  const [selectedContactId, setSelectedContactId] = usePersistedState('standingOrders.contactModalId', null);

  // Slideout buttons
  useSlideOutButton();

  // Drag-to-resize columns (11 cols: expand, customer, product, additive,
  // qty, type, frequency, last/next, notes, status, actions).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('standingOrders', 11);

  // Distinct values per filterable column across the whole result set.
  const [facets, setFacets] = useState({});

  const cols = useColumnFilters(standingOrders, COLUMN_FILTERS, {
    facets,
    storageKey: 'standingOrders.columnFilters'
  });

  // A rep's phone: one card per standing order, opening it read only (1.12.0).
  const repMobile = useRepMobile();
  const [viewingOrder, setViewingOrder] = useState(null);
  // A rep asks the office for a change (rep order requests, 30 Oct 2026).
  const { badge: viewerBadge, canWrite: viewerCanWrite } = useAccess();
  const canRequestChange = viewerBadge === 'REP' && viewerCanWrite('rep_order_requests.send');
  const [changeRequestId, setChangeRequestId] = useState(null);

  const sortMenu = useSortMenu();
  const [modalMode, setModalMode] = useState('edit');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });

  // Expandable row state
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [productHistory, setProductHistory] = useState({});
  const [historyForOrder, setHistoryForOrder] = useState(null); // the order whose product history is open
  const [poLines, setPOLines] = useState({});
  const [loadingPOLines, setLoadingPOLines] = useState({});
  const [expandingAll, setExpandingAll] = useState(false);

  // Selected PO lines for creating orders - keyed by standing order id
  const [selectedPOLines, setSelectedPOLines] = useState({});
  // Per standing order: whether its expanded table includes lines for products
  // it used to hold. On unless someone turns it off.
  const [hideHistoryLines, setHideHistoryLines] = useState({});
  const [statusCounts, setStatusCounts] = useState({ active: 0, held: 0, inactive: 0 });
  // Standing orders naming an archived product (any = main or alternative).
  const [productArchivedCounts, setProductArchivedCounts] = useState({ any: 0, main: 0 });

  // Arriving from a modal's "Find in …" (utils/revealInSearch.js): the term
  // is in the box from the first render.
  const reveal = useReveal('standing-orders');
  const [filters, setFilters] = usePersistedState('standingOrders.filters', {
    search: '',
    scheduleType: '',
    dayOfWeek: '',
    // All statuses by default — an inactive standing order is still one to
    // find, and hiding it made it look deleted.
    status: '',
    // Only those naming an archived product — to work through them.
    productArchived: false,
    limit: '50'
  }, { seed: revealFilters(reveal) });
  // A filter saved before `status` existed has none, and reads as All.
  const statusFilter = filters.status ?? '';

  const [sorting, setSorting] = usePersistedState('standingOrders.sorting', {
    field: 'day_of_week',
    direction: 'asc'
  });

  const [pagination, setPagination] = usePersistedState('standingOrders.pagination', {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadStandingOrders();
  }, []);

  useEffect(() => {
    const delayTimer = setTimeout(() => {
      loadStandingOrders();
    }, 300);
    return () => clearTimeout(delayTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.scheduleType, filters.dayOfWeek, statusFilter, filters.productArchived, filters.limit, cols.queryValue]);

  const loadStandingOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 10000, // Get all for client-side filtering
        sortField: sorting.field,
        sortDirection: sorting.direction
      });

      if (filters.scheduleType) params.append('scheduleType', filters.scheduleType);
      if (filters.dayOfWeek) params.append('dayOfWeek', filters.dayOfWeek);
      if (statusFilter) params.append('status', statusFilter);
      if (filters.productArchived) params.append('productArchived', 'true');
      if (cols.queryValue) params.append('columnFilters', cols.queryValue);

      const response = await fetch(`${API_URL}/api/standing-orders/search?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setStandingOrders(data.standingOrders || []);
        setFacets(data.facets || {});
        if (data.statusCounts) setStatusCounts(data.statusCounts);
        if (data.productArchivedCounts) setProductArchivedCounts(data.productArchivedCounts);
        const limit = parseInt(filters.limit) || 50;
        setPagination({
          page: 1,
          limit,
          total: (data.standingOrders || []).length,
          totalPages: Math.ceil((data.standingOrders || []).length / limit)
        });
      }
    } catch (error) {
      console.error('Error loading standing orders:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to load standing orders', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '50' }));
    setSorting({ field: 'day_of_week', direction: 'asc' });
    // Column filters are filters too; Clear Filters clears everything.
    cols.clearEverything();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, {
    labels: { scheduleType: 'Schedule', dayOfWeek: 'Day', productArchived: 'Product archived' }
  });
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;

  // A status badge in the header note: every other filter off, that status on.
  const showOnlyStatus = (status) => {
    setFilters(prev => ({
      search: '', scheduleType: '', dayOfWeek: '', status, productArchived: false,
      limit: prev.limit || '50'
    }));
    setSorting({ field: 'day_of_week', direction: 'asc' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSetStatus = async (order, status) => {
    if (statusOf(order) === status) return;
    try {
      const res = await fetch(`${API_URL}/api/standing-orders/${order.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not change the status');
      loadStandingOrders();
    } catch (error) {
      setAlertDialog({ isOpen: true, message: error.message, type: 'error' });
    }
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Client-side filtering and sorting
  const filteredAndSortedOrders = useMemo(() => {
    let filtered = [...standingOrders];

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(order => matchesAllTerms([
        order.customer_name,
        order.account_no,
        order.product_name,
        order.item_code,
        order.customer_code,
        order.product_code,
        // Alternatives count too: searching one supplier's code finds the order.
        ...(order.products || []).flatMap(p => [p.item_code, p.item_name]),
      ], filters.search));
    }

    // Sort — within status: held, then active, then inactive.
    filtered.sort((a, b) => {
      const byStatus = STATUS_ORDER[statusOf(a)] - STATUS_ORDER[statusOf(b)];
      if (byStatus !== 0) return byStatus;
      let aVal = a[sorting.field];
      let bVal = b[sorting.field];

      // Handle nulls
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Numeric comparison for certain fields
      if (['quantity', 'day_of_week', 'interval_value'].includes(sorting.field)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }

      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standingOrders, filters.search, sorting]);

  // Update pagination when filters change
  useEffect(() => {
    const limit = parseInt(filters.limit) || 50;
    setPagination(prev => ({
      ...prev,
      page: 1,
      limit,
      total: filteredAndSortedOrders.length,
      totalPages: Math.ceil(filteredAndSortedOrders.length / limit)
    }));
  }, [filteredAndSortedOrders.length, filters.search, filters.limit]);

  // Paginate
  const paginatedOrders = filteredAndSortedOrders.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  // No row selection on this table, so no selectedIds / bulk actions.
  const ctxMenu = useTableContextMenu({
    rows: paginatedOrders,
    getRowId: (o) => o.id
  });

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleAddNew = () => {
    setSelectedOrder(null);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleEdit = (order, { focusProduct: focus = false } = {}) => {
    setSelectedOrder(order);
    setModalMode('edit');
    setFocusProduct(!!focus);
    setShowModal(true);
  };

  // Every standing order naming an archived product, nothing else.
  const showOnlyArchived = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, productArchived: true, limit: prev.limit || '50' }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = (order) => {
    const customerDisplay = order.customer_name || order.customer_code;
    const productDisplay = order.product_name || order.product_code;

    setConfirmDialog({
      isOpen: true,
      message: `Are you sure you want to delete the standing order for ${customerDisplay} - ${productDisplay}?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await fetch(`${API_URL}/api/standing-orders/${order.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });

          if (response.ok) {
            setAlertDialog({ isOpen: true, message: 'Standing order deleted', type: 'success' });
            loadStandingOrders();
          } else {
            setAlertDialog({ isOpen: true, message: 'Failed to delete standing order', type: 'error' });
          }
        } catch (error) {
          console.error('Error deleting standing order:', error);
          setAlertDialog({ isOpen: true, message: 'Error deleting standing order', type: 'error' });
        }
      }
    });
  };

  const handleSave = (result) => {
    setShowModal(false);
    loadStandingOrders();
    // Saved with its main product still archived: allowed, never silent.
    const warnings = result?.warnings || [];
    if (warnings.length) setAlertDialog({ isOpen: true, message: warnings.join(' '), type: 'warning' });
  };

  const formatFrequency = (order) => {
    if (order.schedule_type === 'weekly') {
      return DAY_NAMES[order.day_of_week] || '-';
    } else {
      if (!order.interval_value || !order.interval_type) return '-';
      return `Every ${order.interval_value} ${order.interval_type}`;
    }
  };

  // Toggle row expand
  const toggleRowExpand = async (orderId, accountId) => {
    const newExpanded = new Set(expandedRows);

    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);

      // Fetch PO lines if not already loaded
      if (!poLines[orderId] && accountId) {
        await fetchPOLines(orderId);
      }
    }

    setExpandedRows(newExpanded);
  };

  // The recent PO lines for THIS standing order's products — its main product
  // and every alternative — rather than for everything the customer buys. Kept
  // per standing order: two orders for one customer want different lines.
  const fetchPOLines = async (orderId) => {
    setLoadingPOLines(prev => ({ ...prev, [orderId]: true }));
    try {
      const response = await fetch(
        `${API_URL}/api/standing-orders/${orderId}/po-lines`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setPOLines(prev => ({ ...prev, [orderId]: data.lines || [] }));
    } catch (error) {
      console.error('Failed to fetch PO lines:', error);
      setPOLines(prev => ({ ...prev, [orderId]: [] }));
    } finally {
      setLoadingPOLines(prev => ({ ...prev, [orderId]: false }));
    }
  };

  // Expand/Collapse All functionality
  /* Every standing order, not just the page and not just what the filters
     leave visible. Expanding "all" and finding page 2 collapsed — or a row
     reappearing closed when a filter is cleared — is the behaviour this is
     meant to remove. standingOrders is the unfiltered set. */
  const handleExpandAll = async () => {
    if (expandedRows.size === standingOrders.length) {
      // Collapse all
      setExpandedRows(new Set());
      return;
    }

    // Expand all - fetch PO lines for all accounts that don't have them yet
    setExpandingAll(true);
    const allIds = new Set(standingOrders.map(order => order.id));

    // Every standing order not yet loaded, fetched in parallel
    const ordersToFetch = standingOrders
      .filter(order => order.account_id && !poLines[order.id])
      .map(order => order.id);

    if (ordersToFetch.length > 0) {
      await Promise.all(ordersToFetch.map(id => fetchPOLines(id)));
    }

    setExpandedRows(allIds);
    setExpandingAll(false);
  };

  /*
   * How long ago, in the terms someone reading a standing-order list thinks in:
   * a fortnightly order is "3 weeks" late, not "21 days".
   */
  const weeksSince = (date) => {
    if (!date) return null;
    const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    return Math.floor(days / 7);
  };

  const agoLabel = (date) => {
    if (!date) return '';
    const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 14) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 9) return `${weeks} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
  };

  /* Loaded on demand and kept — hovering the same row twice should not refetch. */
  const loadProductHistory = useCallback(async (id) => {
    if (productHistory[id]) return;
    try {
      const res = await fetch(`${API_URL}/api/standing-orders/${id}/product-history`, { credentials: 'include' });
      const data = await res.json();
      setProductHistory((prev) => ({ ...prev, [id]: data.history || [] }));
    } catch (_) {
      setProductHistory((prev) => ({ ...prev, [id]: [] }));
    }
  }, [productHistory]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'DRAFT': 'draft',
      'AUTHORISED': 'authorised',
      'BILLED': 'billed',
      'DELETED': 'deleted'
    };
    return `status-badge ${statusMap[status] || 'default'}`;
  };

  // The expanded table's lines as shown: with or without products this order
  // used to hold. Selections index into THIS list.
  const visibleLinesFor = (orderId) => (poLines[orderId] || [])
    .filter(line => !hideHistoryLines[orderId] || !line.is_history);

  const toggleHistoryLines = (orderId, include) => {
    setHideHistoryLines(prev => ({ ...prev, [orderId]: !include }));
    // The indices change with the list, so a selection would point elsewhere.
    setSelectedPOLines(prev => ({ ...prev, [orderId]: new Set() }));
  };

  // Toggle PO line selection for a standing order
  const togglePOLineSelection = (orderId, lineIndex) => {
    setSelectedPOLines(prev => {
      const current = prev[orderId] || new Set();
      const updated = new Set(current);
      if (updated.has(lineIndex)) {
        updated.delete(lineIndex);
      } else {
        updated.add(lineIndex);
      }
      return { ...prev, [orderId]: updated };
    });
  };

  // Select/deselect all PO lines for a standing order
  const toggleAllPOLines = (orderId, selectAll) => {
    const lines = visibleLinesFor(orderId);
    if (selectAll) {
      setSelectedPOLines(prev => ({
        ...prev,
        [orderId]: new Set(lines.map((_, idx) => idx))
      }));
    } else {
      setSelectedPOLines(prev => ({
        ...prev,
        [orderId]: new Set()
      }));
    }
  };

  // Create customer order from standing order
  const handleCreateOrder = (order) => {
    const selected = selectedPOLines[order.id] || new Set();
    const customerPOLines = visibleLinesFor(order.id);

    // Each product carries its own additives. Listed ones travel as the line's
    // additives; a free-text one has nowhere else to go, so it joins the
    // description — "Product Name + Additive".
    const productByCode = new Map((order.products || [])
      .map(p => [String(p.item_code || '').toLowerCase(), p]));

    // No order from an archived product: say which, and what to do instead.
    // A picked line is archived by its code (server) or as the product it is.
    const block = createOrderBlock(order, {
      selectedLines: Array.from(selected).map(idx => customerPOLines[idx]).filter(Boolean).map(line => ({
        ...line,
        item_archived: !!(line.item_archived
          || productByCode.get(String(line.product_code || '').toLowerCase())?.is_archived)
      }))
    });
    if (block) {
      setArchivedPrompt({ order, block });
      return;
    }
    const lineFor = (product, fallback) => {
      const additives = product?.additives || [];
      return {
        product_name: withAdditives(product?.item_name || fallback.name,
          additives.filter(a => a.is_free_text)),
        product_code: product?.item_code || fallback.code,
        item_id: product?.item_id || fallback.itemId || null,
        additives: additives.filter(a => !a.is_free_text),
        // Checked again by the order screen and refused by the save.
        is_archived: !!(product?.is_archived || fallback.archived),
        quantity: fallback.quantity,
        unit_price: fallback.unitPrice,
        supplier_name: fallback.supplierName
      };
    };

    // Build order lines from selected PO lines, or use standing order details
    let orderLines = [];

    if (selected.size > 0) {
      // Use selected PO lines
      orderLines = Array.from(selected).map(idx => {
        const line = customerPOLines[idx];
        return lineFor(productByCode.get(String(line.product_code || '').toLowerCase()), {
          name: line.product_name,
          code: line.product_code,
          quantity: parseFloat(line.quantity) || 0,
          unitPrice: parseFloat(line.unit_price) || 0,
          supplierName: line.supplier_name,
          archived: !!line.item_archived
        });
      });
    } else {
      // Use standing order details — the main product. Alternatives travel
      // with it so the order screen can say what else would do.
      orderLines = [lineFor((order.products || [])[0], {
        name: order.product_name || order.product_code,
        code: order.item_code || order.product_code,
        itemId: order.item_id,
        quantity: parseFloat(order.quantity) || 0,
        unitPrice: 0 // Will need to be priced
      })];
    }
    // Active alternatives only; an archived one is left behind, and said so.
    const { active: alternatives, archived: archivedAlternatives } = alternativesOf(order);

    // Store pre-populated data in sessionStorage
    const prePopulatedData = {
      account_id: order.account_id,
      customer_name: order.customer_name,
      account_no: order.account_no,
      standing_order_id: order.id,
      lines: orderLines,
      alternatives,
      alternatives_note: droppedAlternativesNote(archivedAlternatives)
    };

    sessionStorage.setItem('standingOrderData', JSON.stringify(prePopulatedData));

    // Navigate to create customer order
    if (onNavigate) {
      onNavigate('create-customer-order');
    }
  };


  // Shared by the row's RowActionsMenu and the right-click context menu, so the
  // two surfaces can never drift apart.
  const buildRowActions = (order) => [
    // Never hidden or greyed for an archived product: clicking it explains
    // what to do. Marked, so the row shows the problem before the click.
    mainProductArchived(order)
      ? { key: 'create', icon: 'add_shopping_cart', label: 'Create Customer Order', className: 'action-warning',
          title: `${productLabel(mainProductOf(order))} is archived — an order can't be made until the standing order's product is changed`,
          onClick: () => handleCreateOrder(order) }
      : { key: 'create', icon: 'add_shopping_cart', label: 'Create Customer Order', title: 'Create Customer Order', onClick: () => handleCreateOrder(order) },
    // Moved here from a hover button inside the product code badge — a record
    // to read, not a decoration on the code.
    { key: 'product-history', readSafe: true, icon: 'history', label: 'Product History', title: 'Products this standing order has held before',
      onClick: () => { setHistoryForOrder(order); loadProductHistory(order.id); } },
    // A rep asks the office to change it — a request, not an edit.
    ...(canRequestChange ? [{ key: 'request-change', readSafe: true, icon: 'edit_note', label: 'Request a change',
      title: 'Ask the office to change this standing order', onClick: () => setChangeRequestId(order.id) }] : []),
    { key: 'edit', icon: 'edit', label: 'Edit', title: 'Edit', onClick: () => handleEdit(order) },
    // Status, from the row. The current one is shown disabled rather than left
    // out, so every row offers the same three.
    { key: 'status-active', icon: 'play_circle', label: 'Set Active',
      disabled: statusOf(order) === 'active',
      title: statusOf(order) === 'active' ? 'Already active' : 'Set Active',
      onClick: () => handleSetStatus(order, 'active') },
    { key: 'status-held', icon: 'pause_circle', label: 'Hold',
      disabled: statusOf(order) === 'held',
      title: statusOf(order) === 'held' ? 'Already held' : 'Hold — paused, not finished with',
      onClick: () => handleSetStatus(order, 'held') },
    { key: 'status-inactive', icon: 'block', label: 'Set Inactive',
      disabled: statusOf(order) === 'inactive',
      title: statusOf(order) === 'inactive' ? 'Already inactive' : 'Set Inactive',
      onClick: () => handleSetStatus(order, 'inactive') },
    { key: 'delete', icon: 'delete', label: 'Delete', title: 'Delete', onClick: () => handleDelete(order) },
  ];

  // Summary stats
  const weeklyCount = standingOrders.filter(o => o.schedule_type === 'weekly').length;
  const intervalCount = standingOrders.filter(o => o.schedule_type === 'interval').length;

  return (
    <div className="standing-orders">
      {/* The rep phone shell hides .search-header entirely — see RepMobile.css
          — and this stands in for it. Desktop renders nothing. */}
      <RepMobileFilterReset onReset={handleClearFilters} />
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Manage recurring standing orders for customers
          </span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Weekly: {weeklyCount} | Interval: {intervalCount}
            </span>
            {/* Each status, counted across everything this user can see.
                Clicking one shows that status and nothing else. */}
            <span className="so-status-counts">
              {['active', 'held', 'inactive'].map(st => (
                <button
                  key={st}
                  type="button"
                  data-read-safe
                  className={`status-badge ${st}${statusFilter === st ? ' is-current' : ''}`}
                  title={`Show only ${STATUS_LABEL[st].toLowerCase()} standing orders`}
                  onClick={() => showOnlyStatus(st)}
                >
                  {statusCounts[st] || 0} {STATUS_LABEL[st]}
                </button>
              ))}
              {productArchivedCounts.any > 0 && (
                <button
                  type="button"
                  data-read-safe
                  className={`archived-badge so-archived-count${filters.productArchived ? ' is-current' : ''}`}
                  title={`${productArchivedCounts.any} standing order${productArchivedCounts.any === 1 ? ' names' : 's name'} an archived product`
                    + (productArchivedCounts.main ? ` (${productArchivedCounts.main} can make no order until changed)` : '')
                    + ' — show only those'}
                  onClick={showOnlyArchived}
                >
                  {productArchivedCounts.any} Product archived
                </button>
              )}
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button data-write-action className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['customer', 'account no.', 'customer code', 'product', 'item code', 'product code', 'alternative products']} />
            </label>
            <Input
              type="text"
              placeholder="Customer, account #, product..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Schedule Type</label>
            <Dropdown
              value={filters.scheduleType}
              onChange={(value) => handleFilterChange('scheduleType', value)}
              options={[
                { value: '', label: 'All Types' },
                { value: 'weekly', label: 'Weekly (Mon-Fri)' },
                { value: 'interval', label: 'Interval' }
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Day of Week</label>
            <Dropdown
              value={filters.dayOfWeek}
              onChange={(value) => handleFilterChange('dayOfWeek', value)}
              options={[
                { value: '', label: 'All Days' },
                { value: '1', label: 'Monday' },
                { value: '2', label: 'Tuesday' },
                { value: '3', label: 'Wednesday' },
                { value: '4', label: 'Thursday' },
                { value: '5', label: 'Friday' }
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Status</label>
            <Dropdown
              value={statusFilter}
              onChange={(value) => handleFilterChange('status', value)}
              options={[
                { value: '', label: 'All' },
                { value: 'held', label: 'Held' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
            />
          </div>

          <div className="filter-group so-archived-filter">
            <label htmlFor="so-product-archived">Product archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="so-product-archived"
                  type="checkbox"
                  checked={!!filters.productArchived}
                  onChange={(e) => handleFilterChange('productArchived', e.target.checked)}
                />
                <label htmlFor="so-product-archived" title="Only standing orders naming an archived product"></label>
              </div>
            </div>
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="50"
            />
          </div>
        </div>
      </div>

      <div className='standing-orders-panel'>
        <div className="table-toolbar">
          <button
            className="btn-secondary btn-sm"
            onClick={handleExpandAll}
            disabled={loading || expandingAll || paginatedOrders.length === 0}
          >
            <span className="material-symbols-rounded">
              {expandedRows.size === standingOrders.length && standingOrders.length > 0 ? 'unfold_less' : 'unfold_more'}
            </span>
            {expandingAll ? 'Loading...' : (expandedRows.size === standingOrders.length && standingOrders.length > 0 ? 'Collapse All' : 'Expand All')}
          </button>

          <ResetButton />

          {/* A column filter is invisible from anywhere but the header it was
              set on, and a table quietly missing rows is how people stop
              trusting a screen. */}
          <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />

          {pagination.totalPages > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={changePage}
            />
          )}
        </div>

        <div className="table-container">
          {repMobile ? (
            <RepDocCards loading={loading} count={paginatedOrders.length} empty="No standing orders found">
              {paginatedOrders.map((order) => {
                const card = repStandingOrderCard(order);
                return (
                  <RepDocCard
                    key={order.id}
                    title={card.title}
                    place={card.place}
                    flags={card.flags}
                    chip={card.chip}
                    archived={card.chip.label === 'Stopped'}
                    onOpen={() => setViewingOrder(order)}
                    openLabel={`Open the standing order for ${card.title}`}
                  >
                    <span className="rdc-lines">
                      <span className="rdc-line is-strong">{card.product}</span>
                      <span className="rdc-line">{card.schedule}</span>
                      <span className="rdc-line is-muted">{card.lastNext}</span>
                      {card.notes && <span className="rdc-line is-muted">{card.notes}</span>}
                    </span>
                  </RepDocCard>
                );
              })}
            </RepDocCards>
          ) : loading ? (
            <LoadingSpinner />
          ) : (
            <table className="data-table resizable-table" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
              <ColGroup />
              <thead>
                <tr>
                  <th style={{ width: '30px' }}><ResizeHandle colIndex={0} /></th>
                  {/* The cell shows an account number and a company, and people
                      order by both — so the click offers the choice. */}
                  <th className={`sortable ${cols.headerProps('customer').className || ''}`.trim()}
                      onClick={sortMenu.headerProps('customer').onClick}
                      onContextMenu={cols.headerProps('customer').onContextMenu}
                      title="Click to choose how to sort · right-click to filter">
                    Customer {['customer_name', 'account_no'].includes(sorting.field) && (sorting.direction === 'asc' ? '▲' : '▼')}
                    <ResizeHandle colIndex={1} />
                  </th>
                  <th className={`sortable ${cols.headerProps('product').className || ''}`.trim()}
                      onClick={() => handleSort('product_name')}
                      onContextMenu={cols.headerProps('product').onContextMenu}
                      title="Right-click to filter by product">
                    Product {sorting.field === 'product_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                    <ResizeHandle colIndex={2} />
                  </th>
                  <th className={cols.headerProps('additive').className}
                      onContextMenu={cols.headerProps('additive').onContextMenu}
                      title="Right-click to filter by additive">
                    Additive<ResizeHandle colIndex={3} />
                  </th>
                  <th className="text-right sortable" onClick={() => handleSort('quantity')}>
                    Qty {sorting.field === 'quantity' && (sorting.direction === 'asc' ? '▲' : '▼')}
                    <ResizeHandle colIndex={4} />
                  </th>
                  <th className={`sortable ${cols.headerProps('schedule_type').className || ''}`.trim()}
                      onClick={() => handleSort('schedule_type')}
                      onContextMenu={cols.headerProps('schedule_type').onContextMenu}
                      title="Right-click to filter by type">
                    Type {sorting.field === 'schedule_type' && (sorting.direction === 'asc' ? '▲' : '▼')}
                    <ResizeHandle colIndex={5} />
                  </th>
                  <th className={`sortable ${cols.headerProps('frequency').className || ''}`.trim()}
                      onClick={() => handleSort('day_of_week')}
                      onContextMenu={cols.headerProps('frequency').onContextMenu}
                      title="Right-click to filter by frequency">
                    Frequency {sorting.field === 'day_of_week' && (sorting.direction === 'asc' ? '▲' : '▼')}
                    <ResizeHandle colIndex={6} />
                  </th>
                  <th className={`sortable ${cols.headerProps('due_state').className || ''}`.trim()}
                      style={{ textAlign: 'center' }}
                      onClick={() => handleSort('last_had_at')}
                      onContextMenu={cols.headerProps('due_state').onContextMenu}
                      title="Right-click to filter by up to date or overdue">
                    Last/next {sorting.field === 'last_had_at' && (sorting.direction === 'asc' ? '▲' : '▼')}
                    <ResizeHandle colIndex={7} />
                  </th>
                  <th>Notes<ResizeHandle colIndex={8} /></th>
                  <th className={`status-col no-sort ${cols.headerProps('status').className || ''}`.trim()}
                      onContextMenu={cols.headerProps('status').onContextMenu}
                      title="Right-click to filter by status">
                    Status<ResizeHandle colIndex={9} />
                  </th>
                  <th className="actions-col no-sort"><ResizeHandle colIndex={10} /></th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center">
                      No standing orders found
                    </td>
                  </tr>
                ) : (
                  paginatedOrders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr
                        className={`expandable-row ${statusOf(order) === 'inactive' ? 'inactive-row' : ''}${statusOf(order) === 'held' ? ' held-row' : ''}${mainProductArchived(order) ? ' so-product-archived-row' : ''}`}
                        onClick={() => toggleRowExpand(order.id, order.account_id)}
                        onContextMenu={ctxMenu.onRowContextMenu(order)}
                      >
                        <td className="expand-cell">
                          <span className={`expand-icon ${expandedRows.has(order.id) ? 'expanded' : ''}`}>
                            ▼
                          </span>
                        </td>
                        <td>
                          <span className="name-with-badge">
                            <span
                              className={`account-badge-inline${!order.account_no ? ' empty' : ''}`}
                              style={(order.account_id && order.account_no) ? { cursor: 'pointer' } : undefined}
                              onClick={(order.account_id && order.account_no) ? (e) => { e.stopPropagation(); setSelectedContactId(order.account_id); } : undefined}
                            >
                              {order.account_no || '\u00A0'}
                            </span>
                            {order.customer_name ? (
                              <span
                                className="contact-link"
                                onClick={(e) => { e.stopPropagation(); if (order.account_id) setSelectedContactId(order.account_id); }}
                              >
                                {order.customer_name}
                              </span>
                            ) : (
                              <span className="unmatched-code" title="Customer not found in database">
                                {order.customer_code}
                              </span>
                            )}
                          </span>
                          {order.delivery_address && (
                            <>
                            <span className="address-with-space">
                              <span className={'account-badge-inline empty'}></span>
                              <span className="delivery-address">{order.delivery_address}</span>
                            </span>  
                            </>
                          )}
                        </td>
                        <td data-label="Product">
                          {/* The main product, its code beside the name. */}
                          <div className="so-main-product">
                            {order.product_name || (
                              <span className="unmatched-code" title="Product not found in database">
                                {order.product_code}
                              </span>
                            )}
                            {order.product_name && order.item_code && (
                              <span className="product-code">{order.item_code}</span>
                            )}
                            {mainProductArchived(order) && <ArchivedProduct main />}
                          </div>
                          {/* The alternatives this order also accepts, after the main one. */}
                          {(order.products || []).length > 1 && (
                            <div className="so-alt-products">
                              {order.products.slice(1).map(p => (
                                <span className="so-alt-product" key={p.item_id} title="Also accepted — the same delivery from another supplier">
                                  <span className="so-alt-or">or</span>
                                  {withAdditives(`${p.item_name}${p.item_code ? ` (${p.item_code})` : ''}`, p.additives)}
                                  {p.is_archived && <ArchivedProduct />}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td data-label="Additive">
                          {/* The main product's additives; an alternative's own
                              read beside it in the product cell. */}
                          {withAdditives('', (order.products || [])[0]?.additives)}
                        </td>
                        <td data-label="Qty" className="text-right">{formatQuantity(order.quantity)}</td>
                        <td data-label="Schedule">
                          <span className={`schedule-badge ${order.schedule_type}`}>
                            {order.schedule_type === 'weekly' ? 'Weekly' : 'Interval'}
                          </span>
                        </td>
                        <td data-label="Frequency">{formatFrequency(order)}</td>
                        {/* When this customer last actually had this product.
                            The question anyone reading the list is asking: a
                            weekly order that has not moved in six weeks is the
                            one worth a phone call. */}
                        {/* An order already on the books outranks how long it
                            has been: the point of the column is that nobody
                            raises a second one for a delivery already coming. */}
                        <td data-label="Last / next" className="last-had-cell">
                          {order.next_due_at ? (
                            <>
                              <span className="next-due">
                                <span className="material-symbols-rounded">event_available</span>
                                {formatDate(order.next_due_at)}
                              </span>
                              <span className="product-code">
                                <span>on order</span>
                                <span>{order.next_due_reference ? `${order.next_due_reference}` : ''}</span>
                              </span>
                            </>
                          ) : order.last_had_at ? (
                            <>
                              <span className={weeksSince(order.last_had_at) >= 6 ? 'last-had-stale' : undefined}>
                                {formatDate(order.last_had_at)}
                              </span>
                              <span className="product-code">
                                <span>{agoLabel(order.last_had_at)}</span>
                                <span>{order.last_had_reference ? `${order.last_had_reference}` : ''}</span>
                              </span>
                            </>
                          ) : <span className="last-had-never">Never</span>}
                        </td>
                        <td data-label="Notes">{order.notes && <span className="notes-value">{order.notes}</span>}</td>
                        <td data-label="Status" className="status-col">
                          <span className={`status-badge ${statusOf(order)}`}>
                            {STATUS_LABEL[statusOf(order)]}
                          </span>
                        </td>
                        <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu actions={buildRowActions(order)} />
                        </td>
                      </tr>
                      <tr className={`expanded-content ${expandedRows.has(order.id) ? 'expanded' : ''}`}>
                        <td colSpan="11">
                          <div className="po-lines-container">
                            {!loadingPOLines[order.id] && poLines[order.id] && (
                              <div className="po-lines-toolbar">
                                <div className="checkbox-container">
                                  <div className="check light">
                                    <input
                                      id={`so-history-lines-${order.id}`}
                                      type="checkbox"
                                      checked={!hideHistoryLines[order.id]}
                                      onChange={(e) => toggleHistoryLines(order.id, e.target.checked)}
                                    />
                                    <label htmlFor={`so-history-lines-${order.id}`}></label>
                                  </div>
                                </div>
                                <label htmlFor={`so-history-lines-${order.id}`} className="po-lines-toolbar-label">
                                  Include products this standing order used to hold
                                  {poLines[order.id].some(l => l.is_history) ? '' : ' (none)'}
                                </label>
                              </div>
                            )}
                            {loadingPOLines[order.id] ? (
                              <div className="loading-lines">Loading PO lines...</div>
                            ) : visibleLinesFor(order.id).length > 0 ? (
                              <table className="po-lines-table">
                                <thead>
                                  <tr>
                                    <th className="checkbox-col" style={{ width: '30px', textAlign: 'center', padding: 'var(--spacing-sm) 0 var(--spacing-sm) var(--spacing-sm)' }}>
                                      <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                                        <input
                                          type="checkbox"
                                          className="tick-check-input"
                                          id={`select-all-po-${order.id}`}
                                          checked={(selectedPOLines[order.id]?.size || 0) === visibleLinesFor(order.id).length}
                                          onChange={(e) => toggleAllPOLines(order.id, e.target.checked)}
                                          title="Select all for order"
                                        />
                                        <label htmlFor={`select-all-po-${order.id}`} className="tick-check">
                                          <svg width="16px" height="16px" viewBox="0 0 18 18">
                                            <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                                            <polyline points="1 9 7 14 15 4"></polyline>
                                          </svg>
                                        </label>
                                      </label>
                                    </th>
                                    <th>PO #</th>
                                    <th>Date</th>
                                    <th>Product</th>
                                    <th>Delivery Date</th>
                                    <th className="text-right">Qty</th>
                                    <th className="text-right">Unit</th>
                                    <th className="text-right">VAT</th>
                                    <th className="text-right">Total</th>
                                    <th>PO Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visibleLinesFor(order.id).map((line, idx) => (
                                    <tr key={idx} className={line.is_history ? 'is-history-line' : undefined}>
                                      <td className="checkbox-col" style={{ textAlign: 'center', padding: 'var(--spacing-sm)' }}>
                                        <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                                          <input
                                            type="checkbox"
                                            className="tick-check-input"
                                            id={`select-po-${order.id}-${idx}`}
                                            checked={selectedPOLines[order.id]?.has(idx) || false}
                                            onChange={() => togglePOLineSelection(order.id, idx)}
                                            title="Include in customer order"
                                          />
                                          <label htmlFor={`select-po-${order.id}-${idx}`} className="tick-check">
                                            <svg width="16px" height="16px" viewBox="0 0 18 18">
                                              <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                                              <polyline points="1 9 7 14 15 4"></polyline>
                                            </svg>
                                          </label>
                                        </label>
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <span className="po-number">{line.purchase_order_number}</span>
                                          {line.sent_to_contact === true && (
                                            <span
                                              className="material-symbols-rounded"
                                              style={{ fontSize: '14px', color: 'var(--color-success)', cursor: 'help' }}
                                              title="Sent to supplier"
                                            >
                                              mail
                                            </span>
                                          )}
                                          {line.sent_to_contact === false && (
                                            <span
                                              className="material-symbols-rounded"
                                              style={{ fontSize: '14px', color: 'var(--color-error)', cursor: 'help' }}
                                              title="Not sent to supplier"
                                            >
                                              unsubscribe
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td>{formatDate(line.po_date)}</td>
                                      <td>
                                        <div>{line.product_name}</div>
                                        {line.product_code && (
                                          <div className="product-code-with-history">
                                            <span className="product-code">{line.product_code}</span>
                                            {line.is_history && (
                                              <span className="material-symbols-rounded so-history-line-icon"
                                                    title="A product this standing order used to hold">history</span>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      <td>{formatDate(line.delivery_date)}</td>
                                      <td className="text-right">{formatQuantity(line.quantity || 0)}</td>
                                      <td className="text-right">
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                          {formatPrice(line.unit_price || 0)}
                                          {line.price_accepted === true && (
                                            <span
                                              className="material-symbols-rounded"
                                              style={{ fontSize: '14px', color: 'var(--color-success)', cursor: 'help' }}
                                              title="Price accepted"
                                            >
                                              check_circle
                                            </span>
                                          )}
                                          {line.price_accepted === false && (
                                            <span
                                              className="material-symbols-rounded"
                                              style={{ fontSize: '14px', color: 'var(--color-error)', cursor: 'help' }}
                                              title="Price not accepted"
                                            >
                                              cancel
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="text-right">{formatCurrency(line.tax_amount)}</td>
                                      <td className="text-right">{formatCurrency(line.line_total_inc_tax || line.line_total)}</td>
                                      <td>
                                        <span className={getStatusBadgeClass(line.po_status)}>
                                          {line.po_status || '-'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : expandedRows.has(order.id) && !loadingPOLines[order.id] ? (
                              <div className="no-lines">No recent purchase orders for this standing order's products</div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {viewingOrder && (
        <RepStandingOrderView
          standingOrder={viewingOrder}
          onClose={() => setViewingOrder(null)}
          onOpenCustomer={viewingOrder.account_id
            ? () => { const id = viewingOrder.account_id; setViewingOrder(null); setSelectedContactId(id); }
            : null}
          onRequestChange={canRequestChange
            ? () => { const id = viewingOrder.id; setViewingOrder(null); setChangeRequestId(id); }
            : null}
        />
      )}
      {changeRequestId && (
        <RepChangeRequestModal
          kind="amend_standing_order"
          targetId={changeRequestId}
          onClose={() => setChangeRequestId(null)}
        />
      )}

      {showModal && (
        <StandingOrderModal
          standingOrder={selectedOrder}
          mode={modalMode}
          focusProduct={focusProduct}
          onClose={() => { setShowModal(false); setFocusProduct(false); loadStandingOrders(); }}
          onSave={(result) => { setFocusProduct(false); handleSave(result); }}
        />
      )}
      <StandingOrderArchivedDialog
        prompt={archivedPrompt}
        onClose={() => setArchivedPrompt(null)}
        onEdit={(order) => { setArchivedPrompt(null); handleEdit(order, { focusProduct: true }); }}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {selectedContactId && (
        <ContactModal
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
        />
      )}

      {/* Table context menu (right-click) */}
      {historyForOrder && (
        <Modal
          isOpen
          onClose={() => setHistoryForOrder(null)}
          title={`Product history — ${historyForOrder.customer_name || historyForOrder.customer_code || 'Standing order'}`}
          size="sm"
          className="so-product-history"
        >
          {!productHistory[historyForOrder.id] ? (
            <p className="so-history-empty">Loading…</p>
          ) : productHistory[historyForOrder.id].length === 0 ? (
            <p className="so-history-empty">This standing order has not held any other product.</p>
          ) : (
            <ul className="so-history-list">
              {productHistory[historyForOrder.id].map((h, i) => (
                <li className="so-history-row" key={i}>
                  <span className="so-history-item">
                    {h.from ? `${h.from.item_code || h.from.id} — ${h.from.item_name || ''}` : '(none)'}
                  </span>
                  <span className="so-history-meta">
                    changed {formatDate(h.changedAt)} by {h.changedBy}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          {
            key: 'expand-all',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: expandedRows.size === standingOrders.length && standingOrders.length > 0 ? 'unfold_less' : 'unfold_more',
            label: expandedRows.size === standingOrders.length && standingOrders.length > 0 ? 'Collapse all' : 'Expand all',
            disabled: loading || expandingAll || paginatedOrders.length === 0,
            onClick: handleExpandAll
          },
          {
            key: 'refresh',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'refresh',
            label: 'Refresh results',
            disabled: loading,
            onClick: loadStandingOrders
          },
          {
            key: 'clear-filters',
            readSafe: true,   // view/filter only — safe for view-only accounts
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: handleClearFilters
          }
        ]}
      />

      <ColumnFilterMenu cols={cols} />

      <SortOptionsMenu
        menu={sortMenu}
        menuKey="customer"
        title="Sort by customer"
        fields={[
          { value: 'account_no', label: 'Account number' },
          { value: 'customer_name', label: 'Customer name' }
        ]}
        field={sorting.field}
        direction={sorting.direction}
        labels={{ asc: 'A to Z / low to high', desc: 'Z to A / high to low' }}
        onChange={(field, direction) => setSorting({ field, direction })}
      />
    </div>
  );
}

export default StandingOrders;
